import React, { FC, MouseEventHandler } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { trackEvent } from 'dibs-tracking';
import { createFragmentContainer, graphql } from 'react-relay';
import authModalLoader from '../../utils/AuthModalLoader';
import {
    CREATE_PASSWORD_MY_ORDERS_FLOW,
    CREATE_PASSWORD_MY_CONVERSATIONS_FLOW,
    LOGIN,
    REQUEST_PASSWORD_EMAIL,
} from '../../authModal/authentication/authFlowConstants';

import styles from './AccountNav-style.scss';

import { AccountNavContent_user as UserType } from './__generated__/AccountNavContent_user.graphql';
import { AccountNavContent_viewer as ViewerType } from './__generated__/AccountNavContent_viewer.graphql';

const trackingObj = {
    category: 'navigation',
    action: 'header navigation click',
};

type ReduxStoreType = {
    header: {
        logoutUser: () => void;
        isEmailOnly: boolean;
    };
};

type AccountNavDropdownContentProps = {
    totalUnreadConversations: number;
    user: UserType;
    viewer: ViewerType;
};

const AccountNavDropdownContent: FC<AccountNavDropdownContentProps> = ({
    totalUnreadConversations,
    viewer,
    user,
}) => {
    const { isMessageCenterEnabled } = viewer;
    const logoutUser = useSelector<ReduxStoreType, () => void>(({ header }) => header.logoutUser);
    const hasEmailToken = useSelector<ReduxStoreType, boolean>(({ header }) => header.isEmailOnly);

    const onLogoutClick: MouseEventHandler<HTMLSpanElement> = () => {
        trackEvent({ ...trackingObj, label: 'user-nav|logout' });
        logoutUser();
    };

    const showAuthModal: (args: { flow: string; url: string }) => void = async ({ flow, url }) => {
        const email = user?.profile?.email;
        const res = await authModalLoader.show({
            flow,
            action: REQUEST_PASSWORD_EMAIL,
            email,
        });

        if (!res.err && res.modal === LOGIN) {
            window.location.href = url;
        }
    };

    return (
        <div className={styles.dropdownContent}>
            <div
                className={styles.dropdownLinkWrapper}
                data-tn="sub-nav-list-item-account-information"
            >
                <a
                    className={styles.dropdownLink}
                    href="/my/account/"
                    data-tn="sub-nav-list-item-link-account-information"
                    onClick={() => {
                        trackEvent({ ...trackingObj, label: 'user-nav|account information' });
                    }}
                >
                    <FormattedMessage
                        id="dbl.Header.AccountNavContent.account_information"
                        defaultMessage="Account Information"
                    />
                </a>
            </div>
            <div className={styles.dropdownLinkWrapper} data-tn="sub-nav-list-item-my-orders">
                <a
                    className={styles.dropdownLink}
                    data-tn="sub-nav-list-item-link-my-orders"
                    onClick={async () => {
                        const url = '/my/orders/';
                        if (hasEmailToken) {
                            await showAuthModal({ flow: CREATE_PASSWORD_MY_ORDERS_FLOW, url });
                        } else {
                            trackEvent({ ...trackingObj, label: 'user-nav|my orders' });
                            window.location.href = url;
                        }
                    }}
                >
                    <FormattedMessage
                        id="dbl.Header.AccountNavContent.my_orders"
                        defaultMessage="My Orders"
                    />
                </a>
            </div>

            {isMessageCenterEnabled && (
                <div
                    className={styles.dropdownLinkWrapper}
                    data-tn="sub-nav-list-item-my-conversations"
                >
                    <a
                        className={styles.dropdownLink}
                        data-tn="sub-nav-list-item-link-my-conversations"
                        onClick={async () => {
                            const url = '/my/conversations/';
                            if (hasEmailToken) {
                                await showAuthModal({
                                    flow: CREATE_PASSWORD_MY_CONVERSATIONS_FLOW,
                                    url,
                                });
                            } else {
                                trackEvent({
                                    ...trackingObj,
                                    label: 'user-nav|my conversations',
                                });
                                window.location.href = url;
                            }
                        }}
                    >
                        <FormattedMessage
                            id="dbl.Header.AccountNavContent.my_conversations"
                            defaultMessage="My Conversations"
                        />
                        {totalUnreadConversations ? (
                            <span className={styles.unreadCount}>
                                {' '}
                                ({totalUnreadConversations})
                            </span>
                        ) : null}
                    </a>
                </div>
            )}

            <div className={styles.dropdownLinkWrapper} data-tn="sub-nav-list-item-logout">
                <span
                    className={styles.dropdownLink}
                    data-tn="sub-nav-list-item-link-logout"
                    onClick={onLogoutClick}
                >
                    <FormattedMessage
                        id="dbl.Header.AccountNavContent.logout"
                        defaultMessage="Logout"
                    />
                </span>
            </div>
        </div>
    );
};

export default createFragmentContainer(AccountNavDropdownContent, {
    viewer: graphql`
        fragment AccountNavContent_viewer on Viewer {
            isMessageCenterEnabled: isEnabledByLocale(featureName: "MESSAGE_CENTER")
        }
    `,
    user: graphql`
        fragment AccountNavContent_user on User {
            profile {
                email
            }
        }
    `,
});
